import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Stethoscope, Plus, Search, Mail, Phone, Activity } from 'lucide-react';
import { toast } from 'sonner';

const SPECIALTIES = ['Internal Medicine', 'Neurology', 'Pediatrics', 'Emergency Medicine', 'Orthopedics', 'Dermatology', 'Gastroenterology', 'Psychiatry', 'Cardiology', 'Pulmonology', 'General Surgery', 'Oncology'];

export default function DoctorsPage() {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', specialty: '', sub_specialty: '', email: '', phone: '', bio: '' });
  const isAdmin = ['super_admin', 'hospital_admin', 'clinic_admin'].includes(user?.role);

  const fetchDoctors = async () => {
    try {
      let url = '/doctors?';
      if (search) url += `search=${search}&`;
      if (specialtyFilter) url += `specialty=${specialtyFilter}`;
      const res = await API.get(url);
      setDoctors(res.data.doctors || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDoctors(); }, [search, specialtyFilter]);

  const handleCreate = async () => {
    try {
      const defaultSlots = [
        { day: 'Monday', start: '09:00', end: '17:00' },
        { day: 'Tuesday', start: '09:00', end: '17:00' },
        { day: 'Wednesday', start: '09:00', end: '17:00' },
        { day: 'Thursday', start: '09:00', end: '17:00' },
        { day: 'Friday', start: '09:00', end: '13:00' },
      ];
      await API.post('/doctors', { ...form, available_slots: defaultSlots });
      toast.success('Doctor added');
      setDialogOpen(false);
      setForm({ name: '', specialty: '', sub_specialty: '', email: '', phone: '', bio: '' });
      fetchDoctors();
    } catch (err) { toast.error('Failed to add doctor'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="doctors-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Doctors Directory</h1>
          <p className="text-slate-500 mt-1">{doctors.length} doctors available</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-doctor-btn" className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-2" /> Add Doctor</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add New Doctor</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Full Name</Label><Input data-testid="doctor-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Dr. Jane Smith" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Specialty</Label>
                    <Select value={form.specialty} onValueChange={v => setForm({ ...form, specialty: v })}>
                      <SelectTrigger data-testid="doctor-specialty"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Sub-Specialty</Label><Input value={form.sub_specialty} onChange={e => setForm({ ...form, sub_specialty: e.target.value })} placeholder="e.g., Sports Medicine" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="doctor@clinic.com" /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1-555-0200" /></div>
                </div>
                <div className="space-y-2"><Label>Bio</Label><Textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="Professional background..." rows={3} /></div>
                <Button data-testid="doctor-submit" onClick={handleCreate} className="w-full bg-sky-600 hover:bg-sky-700">Add Doctor</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="doctor-search" placeholder="Search doctors..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={specialtyFilter || 'all'} onValueChange={v => setSpecialtyFilter(v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="specialty-filter" className="w-48"><SelectValue placeholder="All Specialties" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Specialties</SelectItem>
            {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="doctors-grid">
        {doctors.map(doc => (
          <Card key={doc.id} data-testid={`doctor-card-${doc.id}`} className="border-slate-200 hover:border-sky-200 hover:shadow-md transition-all">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-6 h-6 text-sky-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900">{doc.name}</h3>
                  <Badge variant="secondary" className="mt-1 text-xs">{doc.specialty}</Badge>
                  {doc.sub_specialty && <span className="text-xs text-slate-500 ml-1">/ {doc.sub_specialty}</span>}
                  <p className="text-xs text-slate-500 mt-2 line-clamp-2">{doc.bio}</p>
                  <div className="flex items-center gap-3 mt-3">
                    {doc.email && <span className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{doc.email}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={doc.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} variant="secondary">
                      {doc.is_available ? 'Available' : 'Unavailable'}
                    </Badge>
                    {doc.available_slots?.length > 0 && <span className="text-[10px] text-slate-400">{doc.available_slots.map(s => s.day.slice(0, 3)).join(', ')}</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {doctors.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">
            <Stethoscope className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No doctors found</p>
          </div>
        )}
      </div>
    </div>
  );
}
